import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, of, switchMap, startWith } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { InvoiceRelativeToBilledPeriod, CompanyProduct } from 'src/app/models/company_product.model';
import { HoldedChartOfAccount } from 'src/app/models/holded_chart_of_account.model';
import { HoldedContact } from 'src/app/models/holded_contact.model';

interface InvoiceRelativeOption {
  value: InvoiceRelativeToBilledPeriod;
  label: string;
  description: string;
}

interface ChartOfAccountOption {
  account: HoldedChartOfAccount;
  label: string;
}

@Component({
  selector: 'app-company-product-settings-dialog',
  templateUrl: './company-product-settings-dialog.component.html',
  styleUrl: './company-product-settings-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatFormField,
    MatLabel,
    MatHint,
    MatInput,
    MatSelect,
    MatOption,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatButton,
    MatProgressSpinner,
  ],
})
export class CompanyProductSettingsDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<CompanyProductSettingsDialogComponent, boolean>);
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  readonly data = inject<{ company_product_id: number }>(MAT_DIALOG_DATA);

  readonly invoice_relative_options: InvoiceRelativeOption[] = [
    {
      value: 'in_advance',
      label: 'Por adelantado',
      description: 'La factura se emite el mes anterior al periodo trabajado.',
    },
    {
      value: 'same_month',
      label: 'Mismo mes',
      description: 'La factura se emite dentro del mismo mes en que se realiza el trabajo.',
    },
    {
      value: 'in_arrears',
      label: 'A mes vencido',
      description: 'La factura se emite el mes posterior al periodo trabajado.',
    },
  ];

  readonly saving = signal(false);

  readonly form = this.fb.nonNullable.group({
    invoice_relative_to_billed_period: this.fb.nonNullable.control<InvoiceRelativeToBilledPeriod>('same_month', Validators.required),
    holded_accounting_account_id: this.fb.control<string | null>(null),
    company_holded_id: this.fb.control<string | null>(null),
  });

  readonly holded_account_input = new FormControl<string | HoldedChartOfAccount>('', { nonNullable: true });
  readonly holded_contact_input = new FormControl<string | HoldedContact>('', { nonNullable: true });

  readonly company_product_signal = toSignal(
    this.api.getCompanyProduct(String(this.data.company_product_id), { with: 'company' }).pipe(
      catchError(() => {
        this.snack_bar.open('No se pudo cargar el producto.', '', { duration: 3000 });
        return of(null);
      })
    ),
    { initialValue: null as CompanyProduct | null }
  );

  readonly chart_of_accounts_loading = signal(true);

  readonly chart_of_accounts = toSignal(
    this.api.listHoldedChartOfAccounts().pipe(
      catchError(() => {
        this.snack_bar.open('No se pudo cargar el plan contable de Holded.', '', { duration: 3000 });
        return of([] as HoldedChartOfAccount[]);
      }),
      finalize(() => this.chart_of_accounts_loading.set(false))
    ),
    { initialValue: [] as HoldedChartOfAccount[] }
  );

  readonly holded_account_filter_text = toSignal(
    this.holded_account_input.valueChanges.pipe(
      startWith(this.holded_account_input.value),
      switchMap(value => of(this.toAccountText(value)))
    ),
    { initialValue: '' }
  );

  readonly filtered_chart_of_accounts = computed<ChartOfAccountOption[]>(() => {
    const text = this.holded_account_filter_text().toLowerCase();
    const accounts = this.chart_of_accounts();
    const matches =
      text.trim() === ''
        ? accounts
        : accounts.filter(account => {
            const name = (account.name ?? '').toLowerCase();
            const number = this.accountNumberText(account).toLowerCase();
            return name.includes(text) || number.includes(text);
          });
    return matches.map(account => ({ account, label: this.formatAccountLabel(account) }));
  });

  readonly chart_of_accounts_empty = computed<boolean>(() => {
    if (this.chart_of_accounts_loading()) {
      return false;
    }
    return this.filtered_chart_of_accounts().length === 0;
  });

  readonly holded_contacts_loading = signal(false);
  readonly holded_contacts_results = signal<HoldedContact[]>([]);

  readonly company_product_loaded = computed(() => this.company_product_signal() !== null);

  private readonly form_initialised = signal(false);

  constructor() {
    this.holded_contact_input.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr) => this.toContactText(prev) === this.toContactText(curr)),
        switchMap(value => {
          const text = this.toContactText(value).trim();
          if (text.length < 2) {
            this.holded_contacts_results.set([]);
            return of([] as HoldedContact[]);
          }
          this.holded_contacts_loading.set(true);
          return this.api.searchHoldedContacts(text, 25).pipe(
            catchError(() => {
              this.snack_bar.open('No se pudieron cargar los contactos de Holded.', '', { duration: 3000 });
              return of([] as HoldedContact[]);
            }),
            finalize(() => this.holded_contacts_loading.set(false))
          );
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(results => this.holded_contacts_results.set(results));

    effect(() => {
      const company_product = this.company_product_signal();
      if (company_product !== null && !this.form_initialised()) {
        this.applyCompanyProductToForm(company_product);
        this.form_initialised.set(true);
      }
    });

    effect(() => {
      const company_product = this.company_product_signal();
      const accounts = this.chart_of_accounts();
      const current = this.holded_account_input.value;
      const target_id = company_product?.holded_accounting_account_id;
      const needs_hydration = typeof current === 'string' && target_id != null && accounts.length > 0;
      if (needs_hydration) {
        const match = accounts.find(account => String(account.id) === target_id);
        if (match !== undefined) {
          this.holded_account_input.setValue(match, { emitEvent: false });
        }
      }
    });
  }

  displayAccount = (account: HoldedChartOfAccount | string | null): string => {
    if (account == null) {
      return '';
    }
    if (typeof account === 'string') {
      return account;
    }
    return this.formatAccountLabel(account);
  };

  displayContact = (contact: HoldedContact | string | null): string => {
    if (contact == null) {
      return '';
    }
    if (typeof contact === 'string') {
      return contact;
    }
    return contact.name;
  };

  onAccountSelected(account: HoldedChartOfAccount) {
    this.form.controls.holded_accounting_account_id.setValue(String(account.id));
    this.holded_account_input.setValue(account);
  }

  onAccountInputBlur() {
    const current = this.holded_account_input.value;
    if (typeof current === 'string' && current.trim() === '') {
      this.form.controls.holded_accounting_account_id.setValue(null);
    }
  }

  onContactSelected(contact: HoldedContact) {
    this.form.controls.company_holded_id.setValue(String(contact.id));
    this.holded_contact_input.setValue(contact);
  }

  onContactInputBlur() {
    const current = this.holded_contact_input.value;
    if (typeof current === 'string' && current.trim() === '') {
      this.form.controls.company_holded_id.setValue(null);
    }
  }

  close() {
    this.dialog_ref.close(false);
  }

  save() {
    if (this.form.invalid || this.saving()) {
      return;
    }

    const company_product = this.company_product_signal();
    if (company_product === null) {
      return;
    }

    this.saving.set(true);

    const payload = this.form.getRawValue();

    const companyProductUpdate$ = this.api.updateCompanyProduct(company_product.id, {
      invoice_relative_to_billed_period: payload.invoice_relative_to_billed_period,
      holded_accounting_account_id: payload.holded_accounting_account_id,
    });

    const companyUpdate$ = this.api.updateCompany(String(company_product.company_id), {
      holded_id: payload.company_holded_id,
    });

    forkJoin([companyProductUpdate$, companyUpdate$])
      .pipe(
        catchError(() => {
          this.snack_bar.open('No se pudo guardar la configuración.', '', { duration: 3000 });
          return of(null);
        }),
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(result => {
        if (result === null) {
          return;
        }
        this.snack_bar.open('Configuración guardada.', '', { duration: 2000 });
        this.dialog_ref.close(true);
      });
  }

  private applyCompanyProductToForm(company_product: CompanyProduct) {
    const initial_period: InvoiceRelativeToBilledPeriod = company_product.invoice_relative_to_billed_period ?? 'same_month';
    this.form.patchValue({
      invoice_relative_to_billed_period: initial_period,
      holded_accounting_account_id: company_product.holded_accounting_account_id ?? null,
      company_holded_id: company_product.company?.holded_id ?? null,
    });

    if (company_product.holded_accounting_account_id != null) {
      this.holded_account_input.setValue(company_product.holded_accounting_account_id, { emitEvent: false });
    }

    if (company_product.company?.holded_id != null) {
      this.holded_contact_input.setValue(company_product.company.holded_id, { emitEvent: false });
    }
  }

  private toAccountText(value: string | HoldedChartOfAccount | null): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return this.formatAccountLabel(value);
  }

  private toContactText(value: string | HoldedContact | null): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return value.name;
  }

  private formatAccountLabel(account: HoldedChartOfAccount): string {
    const number = this.accountNumberText(account);
    const name = account.name ?? '';
    if (number !== '' && name !== '') {
      return `${number} - ${name}`;
    }
    return number || name || String(account.id);
  }

  private accountNumberText(account: HoldedChartOfAccount): string {
    const raw = account.num ?? account.number;
    return raw !== undefined && raw !== null ? String(raw) : '';
  }
}
